import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore, apiClient } from '@dukene/shared';
import { Mail, Lock, AlertCircle, CheckCircle2, ArrowRight, TrendingUp, Users, BarChart3, Eye, EyeOff } from 'lucide-react';
import { renderGoogleIdentityButton } from '../../lib/googleIdentity';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function Login() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const loginWithGoogle = useAuthStore((state) => state.loginWithGoogle);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const [retryAfter, setRetryAfter] = useState(0);
  const submittingRef = useRef(false);
  const rateLimitedRef = useRef(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const googleInitializedRef = useRef(false);
  const googleCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (retryAfter <= 0) return;
    const timer = window.setTimeout(() => {
      setRetryAfter(current => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [retryAfter]);

  useEffect(() => {
    if (requiresVerification) {
      googleInitializedRef.current = false;
      return;
    }
    if (googleInitializedRef.current) return;
    let cancelled = false;

    const initializeGoogle = async () => {
      try {
        const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
        const configResponse = await fetch(`${API_URL}/auth/google/client-id`);
        if (!configResponse.ok) return;
        const configResult = await configResponse.json();
        const clientId = configResult.data?.clientId;
        if (!clientId || cancelled) return;

        if (!cancelled && googleButtonRef.current && !googleInitializedRef.current) {
          const cleanup = await renderGoogleIdentityButton(
            googleButtonRef.current,
            clientId,
            async ({ credential }) => {
              try {
                setError('');
                setIsLoading(true);
                const result = await loginWithGoogle(credential);
                navigate(result?.requiresStoreSelection ? '/select-store' : '/');
              } catch (err: any) {
                setError(err.message || 'Google sign-in failed. Please try again.');
              } finally {
                setIsLoading(false);
              }
            },
            'signin_with',
          );
          if (cancelled) {
            cleanup();
            return;
          }
          googleCleanupRef.current = cleanup;
          googleInitializedRef.current = true;
        }
      } catch {
        // Password sign-in remains available if Google Identity cannot load.
      }
    };

    initializeGoogle();
    return () => {
      cancelled = true;
      googleCleanupRef.current?.();
      googleCleanupRef.current = null;
      googleInitializedRef.current = false;
    };
  }, [loginWithGoogle, navigate, requiresVerification]);

  useEffect(() => {
    if (retryAfter === 0 && rateLimitedRef.current) {
      rateLimitedRef.current = false;
      setError('');
    }
  }, [retryAfter]);

  const retryLabel = `${Math.floor(retryAfter / 60)}:${String(retryAfter % 60).padStart(2, '0')}`;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    if (submittingRef.current || retryAfter > 0) return;
    submittingRef.current = true;
    try {
      setError('');
      setIsLoading(true);
      const result = await login(data.email, data.password);
      
      if (result?.requiresVerification) {
        setPendingEmail(data.email);
        setRequiresVerification(true);
        return;
      }
      
      if (result?.requiresStoreSelection) {
        navigate('/select-store');
        return;
      }
      
      navigate('/');
    } catch (err: any) {
      if (err?.statusCode === 429) {
        rateLimitedRef.current = true;
        setRetryAfter(Number(err.retryAfter) || 900);
      }
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendStatus('loading');
      setError('');
      await apiClient.post('/auth/resend-verification', { email: pendingEmail });
      setResendStatus('success');
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email.');
      setResendStatus('idle');
    }
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Grow Your Business',
      description: 'Powerful tools to scale',
    },
    {
      icon: Users,
      title: 'Manage Customers',
      description: 'Track and engage easily',
    },
    {
      icon: BarChart3,
      title: 'Real-time Analytics',
      description: 'Insights at your fingertips',
    },
  ];

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-white border-r border-gray-100 relative">
        <div className="flex flex-col justify-between w-full p-16 xl:p-24">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-black tracking-tight">Dukene</h1>
              <p className="text-xs text-gray-500 font-medium tracking-wide">MERCHANTS</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-16">
            <div>
              <h2 className="text-6xl font-light text-black mb-6 leading-tight tracking-tight">
                Manage Your
                <br />
                Store
              </h2>
              <div className="w-16 h-0.5 bg-black mb-6"></div>
              <p className="text-lg text-gray-600 leading-relaxed font-light">
                Access your merchant dashboard to manage products, orders, and grow your business.
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="flex items-start gap-6">
                    <div className="w-12 h-12 border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 bg-white">
                      <Icon className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <h3 className="text-black font-medium text-base mb-1 tracking-tight">{benefit.title}</h3>
                      <p className="text-gray-500 text-sm font-light">{benefit.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-8 text-xs text-gray-400 font-light">
            <span>© 2024 Dukene</span>
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
            <span>All rights reserved</span>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center bg-white px-8 lg:px-20 xl:px-32 py-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-16">
            <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-black">Dukene</h1>
              <p className="text-xs text-gray-500 font-medium">MERCHANTS</p>
            </div>
          </div>

          {/* Form Header */}
          <div className="mb-12">
            <h2 className="text-4xl font-light text-black mb-3 tracking-tight">
              Welcome Back
            </h2>
            <p className="text-gray-500 text-base font-light">
              Access your merchant dashboard
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-8 border border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-900 text-sm mb-1">{retryAfter > 0 ? 'Please wait before trying again' : 'Sign-in unsuccessful'}</p>
                  <p className="text-sm text-red-700 font-light">{error}</p>
                  {retryAfter > 0 && <p className="text-xs text-red-600 mt-2 font-medium">Try again in {retryLabel}</p>}
                </div>
              </div>
            </div>
          )}

          {requiresVerification ? (
            <div className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-medium text-amber-900 mb-2">Check your email</h3>
                <p className="text-amber-800 text-sm font-light mb-6">
                  We've sent a verification link to <span className="font-medium">{pendingEmail}</span>. 
                  Please verify your email address to access your dashboard.
                </p>
                
                {resendStatus === 'success' ? (
                  <div className="flex items-center justify-center gap-2 text-green-700 text-sm font-medium bg-green-50 py-2 px-4 rounded border border-green-200">
                    <CheckCircle2 className="w-4 h-4" />
                    Verification email sent!
                  </div>
                ) : (
                  <button
                    onClick={handleResendVerification}
                    disabled={resendStatus === 'loading'}
                    className="w-full h-10 bg-white border border-amber-300 text-amber-800 text-sm font-medium rounded-lg hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendStatus === 'loading' ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                )}
              </div>
              
              <button
                onClick={() => {
                  setRequiresVerification(false);
                  setPendingEmail('');
                  setResendStatus('idle');
                }}
                className="w-full text-center text-sm text-gray-500 hover:text-black transition-colors"
              >
                Back to login
              </button>
            </div>
          ) : (
            <>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-black mb-3">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    {...register('email')}
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="h-12 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black pl-12 pr-4 text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed font-light"
                    disabled={isLoading || retryAfter > 0}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-600 mt-2 font-light">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-black mb-3">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    {...register('password')}
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="h-12 w-full rounded-lg border border-gray-200 bg-white text-black focus:border-black focus:outline-none focus:ring-1 focus:ring-black pl-12 pr-12 text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed font-light"
                    disabled={isLoading || retryAfter > 0}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-600 mt-2 font-light">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black accent-black"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-500 font-light">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <Link
                    to="/auth/forgot-password"
                    className="font-medium text-black hover:text-gray-600 transition-colors"
                  >
                    Forgot your password?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || retryAfter > 0}
                className="group relative w-full h-12 flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-black hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : retryAfter > 0 ? (
                  <span>Try again in {retryLabel}</span>
                ) : (
                  <span className="flex items-center gap-2">
                    Sign in to your store
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                )}
              </button>
            </form>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-4 text-gray-400">OR</span></div>
            </div>
            <div ref={googleButtonRef} className="flex min-h-11 w-full justify-center overflow-hidden" />
            </>
          )}

          {/* Footer Links */}
          <div className="mt-12 space-y-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-100" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-4 text-gray-400 font-light tracking-wide">
                  NEW TO DUKENE?
                </span>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-black font-light group transition-colors"
              >
                Create an account
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
